import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

// Connects to data-controller="magic-link"
// Handles magic link form submission with loading and success states
// Matches the behavior of the original MagicLinkButton React component
export default class extends Controller {
  static targets = ["email", "button", "buttonText"]
  static values = {
    successDelay: { type: Number, default: 3000 },
  }
  static classes = ["primary", "success"]

  declare readonly hasEmailTarget: boolean
  declare readonly emailTarget: HTMLInputElement
  declare readonly hasButtonTarget: boolean
  declare readonly buttonTarget: HTMLButtonElement
  declare readonly hasButtonTextTarget: boolean
  declare readonly buttonTextTarget: HTMLElement
  declare successDelayValue: number
  declare readonly primaryClasses: string[]
  declare readonly successClasses: string[]

  private state: "idle" | "sending" | "success" = "idle"
  private timeoutId: ReturnType<typeof setTimeout> | null = null
  private originalButtonText = ""

  connect() {
    if (this.hasButtonTextTarget) {
      this.originalButtonText = this.buttonTextTarget.innerHTML
    }
  }

  disconnect() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
    }
  }

  async submit(event: Event) {
    event.preventDefault()

    if (this.state === "sending") return

    const form = this.element as HTMLFormElement
    const email = this.hasEmailTarget ? this.emailTarget.value : ""

    if (!email?.trim()) {
      return
    }

    this.setState("sending")

    // Add a small delay to make the sending state visible (like original)
    await new Promise((resolve) => setTimeout(resolve, 100))

    try {
      const response = await post(form.action, {
        body: new FormData(form),
        responseKind: "html",
      })

      if (response.ok || response.redirected) {
        this.setState("success")

        // Clear the email input
        if (this.hasEmailTarget) {
          this.emailTarget.value = ""
        }

        // Show success state for 3 seconds, then close the dialog
        this.timeoutId = setTimeout(() => {
          this.closeDialog()
          // Reset state after closing
          setTimeout(() => this.setState("idle"), 300)
        }, this.successDelayValue)
      } else {
        this.setState("idle")
        await this.showRefusal(response)
      }
    } catch {
      this.setState("idle")
      await this.showRefusal()
    }

    this.dispatch("finished")
  }

  // A refusal must change what the person sees, or they wait for an email
  // that never went out. The server refuses a join without a valid stamp or
  // over its caps (#2283) with a plain-text reason; anything else gets a
  // generic one.
  private async showRefusal(response?: Awaited<ReturnType<typeof post>>) {
    if (!this.hasButtonTextTarget) return

    this.buttonTextTarget.textContent =
      response?.contentType === "text/plain"
        ? (await response.text).trim()
        : "Something went wrong. Please try again."
  }

  private setState(state: "idle" | "sending" | "success") {
    this.state = state

    if (!this.hasButtonTarget || !this.hasButtonTextTarget) return

    const button = this.buttonTarget
    const buttonText = this.buttonTextTarget

    // Remove all CSS classes first
    button.classList.remove(...this.primaryClasses, ...this.successClasses)

    switch (state) {
      case "sending":
        button.disabled = true
        button.classList.add(...this.primaryClasses)
        buttonText.innerHTML = "Sending..."
        break

      case "success":
        button.disabled = true
        button.classList.add(...this.successClasses)
        buttonText.innerHTML = `<wa-icon name="circle-check" library="lucide" class="mr-2 inline-block align-text-bottom" style="font-size: 1rem;" aria-hidden="true"></wa-icon>Check your inbox!`
        break

      default:
        button.disabled = false
        button.classList.add(...this.primaryClasses)
        buttonText.innerHTML = this.originalButtonText
    }
  }

  private closeDialog() {
    // Find the native dialog element and close it
    const dialog = this.element
      .closest("el-dialog")
      ?.querySelector("dialog") as HTMLDialogElement | null
    if (dialog) {
      dialog.close()
    }
  }
}
